import { useCallback, useEffect } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useVisualizationImage } from './useVisualizationImage';
import type { DocumentPresentationSlide } from '../../core/types/data-models';

function SlideThumb({
  slide,
  selected,
  onClick,
}: {
  slide: DocumentPresentationSlide;
  selected: boolean;
  onClick: () => void;
}) {
  const { src } = useVisualizationImage(slide.documentVisualizationId);

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={`Go to slide ${slide.orderIndex + 1}: ${slide.title}`}
      aria-current={selected}
      sx={{
        width: 88,
        height: 50,
        p: 0,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        opacity: selected ? 1 : 0.65,
        bgcolor: 'rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.15s',
        '&:hover': { opacity: 1 },
      }}
    >
      {src ? (
        <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Typography
          sx={{ fontSize: 9, px: 0.5, color: 'common.white', lineHeight: 1.2, textAlign: 'center' }}
        >
          {slide.title}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Full-screen slide show: one slide in the center, arrows and keyboard
 * navigation, and a thumbnail strip to jump straight to a slide.
 */
export function PresentationModeDialog({
  open,
  onClose,
  slides,
  index,
  onIndexChange,
}: {
  open: boolean;
  onClose: () => void;
  slides: DocumentPresentationSlide[];
  index: number;
  onIndexChange: (index: number) => void;
}) {
  const slide = slides[index];
  const { src, isLoading } = useVisualizationImage(slide?.documentVisualizationId, open);

  const goTo = useCallback(
    (next: number) => {
      if (next >= 0 && next < slides.length) onIndexChange(next);
    },
    [slides.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goTo(index + 1);
      if (e.key === 'ArrowLeft') goTo(index - 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, index, goTo]);

  if (!slide) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'rgba(17,17,19,0.98)' } }}
    >
      <Stack sx={{ height: '100%', color: 'common.white' }}>
        {/* Top bar — slide number + close */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {index + 1} / {slides.length}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose} sx={{ color: 'common.white' }} aria-label="Close presentation">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Slide */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, gap: { xs: 0.5, sm: 2 } }}
        >
          <IconButton
            onClick={() => goTo(index - 1)}
            disabled={index === 0}
            aria-label="Previous slide"
            sx={{ color: 'common.white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
          >
            <ChevronLeftIcon sx={{ fontSize: 40 }} />
          </IconButton>

          <Stack
            spacing={2}
            sx={{ flex: 1, minWidth: 0, height: '100%', py: 2, alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {slide.documentVisualizationId == null ? (
                <ImageOutlinedIcon sx={{ fontSize: 64, opacity: 0.2 }} />
              ) : isLoading || !src ? (
                <Skeleton variant="rectangular" sx={{ width: '70%', height: '100%', bgcolor: 'rgba(255,255,255,0.08)' }} />
              ) : (
                <Box
                  component="img"
                  src={src}
                  alt={slide.title}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
                />
              )}
            </Box>

            <Box sx={{ width: '100%', maxWidth: 900, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>
                {slide.title}
              </Typography>
              {slide.body && (
                <Box
                  sx={{
                    mt: 1,
                    fontSize: 15,
                    opacity: 0.85,
                    textAlign: 'left',
                    display: 'inline-block',
                    '& ul,& ol': { pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
                    '& p': { m: 0 },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{slide.body}</ReactMarkdown>
                </Box>
              )}
            </Box>
          </Stack>

          <IconButton
            onClick={() => goTo(index + 1)}
            disabled={index === slides.length - 1}
            aria-label="Next slide"
            sx={{ color: 'common.white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
          >
            <ChevronRightIcon sx={{ fontSize: 40 }} />
          </IconButton>
        </Stack>

        {/* Thumbnail strip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ p: 1.5, flexShrink: 0, overflowX: 'auto', justifyContent: { xs: 'flex-start', md: 'center' } }}
        >
          {slides.map((s, i) => (
            <SlideThumb key={s.id} slide={s} selected={i === index} onClick={() => onIndexChange(i)} />
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
}
